import type { StoreSettings } from '@/types'

/** Static copy for the launching soon page (not managed in admin). */
export const COMING_SOON_CONTENT = {
  tagline: 'Luxury attars & oriental fragrances — crafted for the soul.',
  subtitle:
    'We are putting the finishing touches on Bazm-e-Musk. A curated collection of premium attars and signature scents awaits you.',
  highlights: [
    'Handcrafted attars, distilled with devotion',
    'Rich, long-lasting oriental blends',
    'Luxury fragrances, bottled for every occasion',
    'Pure ingredients · Zero compromise · Timeless scent',
  ],
  notes: [
    'Khamrah', 'Vampire Blood', 'Al Maryam', 'Arabian Tonka', 'Shanaya', 'Nadir',
  ],
  values: [
    { label: 'Pure Attars', desc: 'Alcohol-free essence' },
    { label: 'Long Lasting', desc: 'All-day projection' },
    { label: 'Handcrafted', desc: 'Small-batch quality' },
  ],
  footerTagline: 'Premium attars · Signature blends · Timeless elegance',
} as const

const DEFAULT_LAUNCH_ISO = '2026-06-15T00:00:00'
const PROGRESS_LEAD_MS = 30 * 24 * 60 * 60 * 1000

export interface ComingSoonSchedule {
  launchDate: Date
  progressStartDate: Date
}

type DateLike = string | { toDate?: () => Date } | undefined

function parseDate(value?: DateLike): Date | null {
  if (value == null) return null
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    const d = value.toDate()
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof value !== 'string' || !value.trim()) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Normalize Firestore / legacy values to ISO for storage and inputs. */
export function normalizeComingSoonIso(value?: DateLike, fallback?: string): string {
  const parsed = parseDate(value)
  if (parsed) return parsed.toISOString()
  if (fallback) {
    const fb = parseDate(fallback)
    if (fb) return fb.toISOString()
  }
  return COMING_SOON_DEFAULTS.comingSoonLaunchAt
}

export function resolveComingSoonSchedule(
  settings: Pick<
    StoreSettings,
    'comingSoonLaunchAt' | 'comingSoonProgressStartAt'
  >
): ComingSoonSchedule {
  const launchDate =
    parseDate(settings.comingSoonLaunchAt) ?? new Date(DEFAULT_LAUNCH_ISO)
  const progressStartDate =
    parseDate(settings.comingSoonProgressStartAt) ??
    new Date(launchDate.getTime() - PROGRESS_LEAD_MS)
  return { launchDate, progressStartDate }
}

/** True once the configured launch moment has been reached. */
export function isStoreLaunched(
  schedule: ComingSoonSchedule,
  now = Date.now()
): boolean {
  return now >= schedule.launchDate.getTime()
}

export function getLaunchProgress(
  schedule: ComingSoonSchedule,
  now = Date.now()
): number {
  const start = schedule.progressStartDate.getTime()
  const end = schedule.launchDate.getTime()
  if (end <= start) return 100
  const pct = ((now - start) / (end - start)) * 100
  return Math.min(100, Math.max(0, pct))
}

/** Format ISO date for `<input type="datetime-local" />` in local timezone. */
export function toDatetimeLocalValue(iso?: DateLike): string {
  const d = parseDate(iso)
  if (!d) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Parse `<input type="datetime-local" />` value to ISO string. */
export function fromDatetimeLocalValue(value: string): string {
  if (!value.trim()) return ''
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

export const COMING_SOON_DEFAULTS = {
  comingSoonEnabled: false,
  comingSoonLaunchAt: new Date(DEFAULT_LAUNCH_ISO).toISOString(),
  comingSoonProgressStartAt: new Date(
    new Date(DEFAULT_LAUNCH_ISO).getTime() - PROGRESS_LEAD_MS
  ).toISOString(),
} as const
